import { useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch } from "../../store/hook";
import { forgotPassword } from "../../store/slices/authentificationSlice";
import { Mail, CheckCircle } from "lucide-react";

const ForgotPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await dispatch(forgotPassword(email)).unwrap();
      setSuccess(true);
    } catch (err: any) {
      setError(err || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-10 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Email envoyé</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Si cette adresse existe, vous recevrez un lien de réinitialisation valable <strong>1 heure</strong>.
          </p>
          <Link to="/signin" className="inline-block px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900">
      <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 sm:px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Mot de passe oublié</h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Entrez votre email pour recevoir un lien de réinitialisation.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
                <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Votre adresse email"
                />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full flex justify-center py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? "Envoi en cours..." : "Envoyer le lien"}
            </button>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              <Link to="/signin" className="font-medium text-indigo-600 hover:text-indigo-500">← Retour à la connexion</Link>
            </p>
          </form>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-indigo-600 px-12">
        <div className="max-w-md text-white">
          <h1 className="text-4xl font-bold leading-tight">Récupération de compte</h1>
          <p className="mt-4 text-indigo-100 text-base leading-7">
            Un lien sécurisé valable 1 heure vous sera envoyé par email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
